import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, FolderOpen, FileText } from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";

function flattenItems(items, expandedIds, depth = 0) {
  const flat = [];
  for (const item of items) {
    const isGroup = !!item.children;
    flat.push({ ...item, _depth: depth, _isGroup: isGroup });
    if (item.children && expandedIds.has(item.id)) {
      flat.push(...flattenItems(item.children, expandedIds, depth + 1));
    }
  }
  return flat;
}

function getDescendantIds(item) {
  const ids = [];
  if (item.id) ids.push(item.id);
  if (item.children) {
    for (const child of item.children) {
      ids.push(...getDescendantIds(child));
    }
  }
  return ids;
}

export default function GranularPermissionsSelector({
  items = [],
  value = [],
  onChange,
}) {
  const [expandedIds, setExpandedIds] = useState(() => {
    const set = new Set();
    const walk = (list) => {
      for (const item of list) {
        if (item.id && item.children) set.add(item.id);
        if (item.children) walk(item.children);
      }
    };
    walk(items);
    return set;
  });

  const selectedSet = useMemo(() => new Set(value || []), [value]);
  const flat = useMemo(() => flattenItems(items, expandedIds), [items, expandedIds]);

  function toggleExpand(id) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleToggle(item) {
    const descIds = getDescendantIds(item);
    const all = descIds.every((id) => selectedSet.has(id));
    const next = new Set(selectedSet);
    if (all) {
      for (const id of descIds) next.delete(id);
    } else {
      for (const id of descIds) next.add(id);
    }
    onChange([...next]);
  }

  const allIds = useMemo(() => {
    const ids = [];
    const walk = (list) => {
      for (const item of list) {
        if (item.id) ids.push(item.id);
        if (item.children) walk(item.children);
      }
    };
    walk(items);
    return ids;
  }, [items]);

  function handleSelectAll() {
    onChange([...allIds]);
  }

  function handleClear() {
    onChange([]);
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {value.length} de {allIds.length} item(ns) selecionado(s)
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-xs text-primary hover:underline"
          >
            Selecionar todos
          </button>
          <span className="text-xs text-muted-foreground">|</span>
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-muted-foreground hover:underline"
          >
            Limpar
          </button>
        </div>
      </div>

      {/* Tree */}
      <ScrollArea className="h-full border rounded-lg">
        {flat.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-8">
            Nenhum item de menu encontrado.
            <br />
            Execute o Init do menu (Editor de Menu) primeiro.
          </div>
        )}

        <div className="p-2 space-y-0.5">
          {flat.map((item) => {
            const descIds = getDescendantIds(item);
            const allSel = descIds.every((id) => selectedSet.has(id));
            const someSel = descIds.some((id) => selectedSet.has(id));
            const checked = allSel;
            const indeterminate = item._isGroup && someSel && !allSel;
            const isExpanded = expandedIds.has(item.id);

            return (
              <div
                key={item.id || item.label}
                className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-accent/50 group"
                style={{ paddingLeft: `${12 + item._depth * 20}px` }}
              >
                {/* Expand/collapse for groups */}
                {item._isGroup ? (
                  <button
                    type="button"
                    onClick={() => toggleExpand(item.id)}
                    className="p-0.5 rounded hover:bg-accent flex-shrink-0"
                    tabIndex={-1}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                  </button>
                ) : (
                  <span className="w-5 flex-shrink-0" />
                )}

                <Checkbox
                  id={`perm-${item.id || item.label}`}
                  checked={checked}
                  indeterminate={indeterminate}
                  onCheckedChange={() => handleToggle(item)}
                />

                <Label
                  htmlFor={`perm-${item.id || item.label}`}
                  className="flex items-center gap-2 text-sm cursor-pointer flex-1 min-w-0"
                >
                  <span className="flex-shrink-0 text-muted-foreground">
                    {item._isGroup ? (
                      <FolderOpen className="h-4 w-4" />
                    ) : (
                      <FileText className="h-4 w-4" />
                    )}
                  </span>
                  <span className="truncate">{item.label || item.id}</span>
                  {item._isGroup && item.children && (
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 flex-shrink-0"
                    >
                      {item.children.length}
                    </Badge>
                  )}
                </Label>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Selected IDs summary (collapsed) */}
      {value.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer hover:text-foreground">
            IDs selecionados ({value.length})
          </summary>
          <pre className="mt-1 p-2 bg-muted rounded-md overflow-x-auto max-h-24 text-[10px]">
            {JSON.stringify(value, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
